<script setup lang="ts">
import { computed } from 'vue';
import { AssetContainerState, AssetContainerType } from '@nova/core';

import type { YardAssetDetails } from '@satellite/composables/useYardAssetPresenterV2';

const CONTAINER_WIDTH = 30;
const CONTAINER_HEIGHT = 76;

const CONTAINER_X = 0;
const CONTAINER_Y = 0;

const props = withDefaults(
  defineProps<{
    asset: YardAssetDetails;
    orientation?: 'horizontal' | 'vertical';
    loading?: boolean;
  }>(),
  {
    orientation: 'horizontal',
    loading: false
  }
);

defineOptions({
  inheritAttrs: false
});

const revealWidth = computed(() => {
  const REVEAL_WIDTH_MULTIPLIER_BY_STATE: Record<AssetContainerState, number> = {
    [AssetContainerState.Empty]: 1,
    [AssetContainerState.PartiallyLoaded]: 0.5,
    [AssetContainerState.Full]: 0
  };

  return (REVEAL_WIDTH_MULTIPLIER_BY_STATE[props.asset.state] ?? 1) * CONTAINER_HEIGHT;
});

const svgDefUid = computed(() => `${props.asset.id ?? 'new'}`);

const isAssetTrailer = computed(() => {
  return props.asset.type === AssetContainerType.Trailer;
});

const isAssetContainer = computed(() => {
  return props.asset.type === AssetContainerType.Container;
});

const isAssetOther = computed(() => {
  return !isAssetTrailer.value && !isAssetContainer.value;
});

const isUnknownState = computed(() => {
  return ![
    AssetContainerState.Full,
    AssetContainerState.PartiallyLoaded,
    AssetContainerState.Empty
  ].includes(props.asset.state);
});
</script>

<template>
  <span
    class="yard-asset-state-icon-v2 position-relative"
    v-bind="$attrs"
    :class="{
      'is-full': asset.state === AssetContainerState.Full,
      'is-partially-loaded': asset.state === AssetContainerState.PartiallyLoaded,
      'is-empty': asset.state === AssetContainerState.Empty,
      'is-loading': loading,
      '-horizontal': orientation === 'horizontal',
      '-vertical': orientation === 'vertical'
    }"
    :data-testid="`yard-asset-state-icon-v2`">
    <template v-if="orientation === 'horizontal'">
      <svg viewBox="0 0 76 30" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g v-if="isAssetTrailer" class="truck-trailer is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="3.65"
            y="26.35"
            width="22.7"
            height="68.7"
            stroke="var(--dark-fill-color)"
            stroke-width="1.3"
            transform="rotate(-90 3.65 26.35)" />
          <rect
            x="2.1582"
            y="29.6045"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(-135 2.1582 29.6045)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 3.07031 4)" />
          <rect
            x="74.6855"
            y="0.476567"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(45 74.6855 0.476567)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 74.6035 29.6045)" />
        </g>

        <g v-else-if="isAssetContainer" class="truck-container is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="70.4492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 70.4492 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="65.1484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 65.1484 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="59.8516"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 59.8516 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="54.5508"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 54.5508 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="49.25"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 49.25 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="43.9492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 43.9492 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="38.6484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 38.6484 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="33.3516"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 33.3516 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="28.0508"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 28.0508 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="22.75"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 22.75 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="17.4492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 17.4492 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="12.1484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 12.1484 4)"
            fill="var(--dark-fill-color)" />
          <rect
            x="6.85156"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 6.85156 4)"
            fill="var(--dark-fill-color)" />
        </g>

        <g v-else-if="isAssetOther" class="truck-other is-empty">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'"
            transform="rotate(-90 1.31134e-06 30)" />

          <mask
            :id="`truck-other-mask-h-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="74"
            height="28">
            <rect x="1" y="29" width="28" height="20" transform="rotate(-90 1 29)" fill="white" />
            <rect x="55" y="29" width="28" height="20" transform="rotate(-90 55 29)" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-h-${svgDefUid})`">
            <path
              d="M0.333113 29.4317L76.3153 0.939933"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
            <path
              d="M0.33339 0.566248L76.1553 28.998"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
          </g>
        </g>

        <!-- Everything under a white pixel will be visible; Everything under a black pixel will be invisible -->
        <mask v-if="true" :id="`container-reveal-mask-h-${svgDefUid}`" mask-type="luminance">
          <rect
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_HEIGHT"
            :height="CONTAINER_WIDTH"
            fill="white" />
          <rect
            id="fillingRevealRectHorizontal"
            class="reveal-mask"
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="revealWidth"
            :height="CONTAINER_WIDTH"
            fill="black" />
        </mask>

        <g
          v-if="isAssetTrailer"
          class="truck-trailer is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="3.65"
            y="26.35"
            width="22.7"
            height="68.7"
            stroke="white"
            stroke-width="1.3"
            transform="rotate(-90 3.65 26.35)" />
          <rect
            x="2.1582"
            y="29.6045"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(-135 2.1582 29.6045)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 3.07031 4)" />
          <rect
            x="74.6855"
            y="0.476567"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(45 74.6855 0.476567)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="white"
            transform="matrix(0.707107 -0.707107 -0.707107 -0.707107 74.6035 29.6045)" />
        </g>

        <g
          v-else-if="isAssetContainer"
          class="truck-container is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <rect
            x="70.4492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 70.4492 4)"
            fill="white" />
          <rect
            x="65.1484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 65.1484 4)"
            fill="white" />
          <rect
            x="59.8516"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 59.8516 4)"
            fill="white" />
          <rect
            x="54.5508"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 54.5508 4)"
            fill="white" />
          <rect
            x="49.25"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 49.25 4)"
            fill="white" />
          <rect
            x="43.9492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 43.9492 4)"
            fill="white" />
          <rect
            x="38.6484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 38.6484 4)"
            fill="white" />
          <rect
            x="33.3516"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 33.3516 4)"
            fill="white" />
          <rect
            x="28.0508"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 28.0508 4)"
            fill="white" />
          <rect
            x="22.75"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 22.75 4)"
            fill="white" />
          <rect
            x="17.4492"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 17.4492 4)"
            fill="white" />
          <rect
            x="12.1484"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 12.1484 4)"
            fill="white" />
          <rect
            x="6.85156"
            y="4"
            width="22"
            height="1.3"
            transform="rotate(90 6.85156 4)"
            fill="white" />
        </g>

        <g
          v-else-if="isAssetOther"
          class="truck-other is-full"
          :mask="`url(#container-reveal-mask-h-${svgDefUid})`">
          <rect
            x="1.31134e-06"
            y="30"
            width="30"
            height="76"
            rx="2"
            fill="var(--dark-fill-color)"
            transform="rotate(-90 1.31134e-06 30)" />

          <mask
            :id="`truck-other-mask-h-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="74"
            height="28">
            <rect x="1" y="29" width="28" height="20" transform="rotate(-90 1 29)" fill="white" />
            <rect x="55" y="29" width="28" height="20" transform="rotate(-90 55 29)" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-h-${svgDefUid})`">
            <path d="M0.333113 29.4317L76.3153 0.939933" stroke="white" stroke-width="1.3" />
            <path d="M0.33339 0.566248L76.1553 28.998" stroke="white" stroke-width="1.3" />
          </g>
        </g>

        <rect
          v-else
          x="1.31134e-06"
          y="30"
          width="30"
          height="76"
          rx="2"
          fill="var(--light-fill-color)"
          transform="rotate(-90 1.31134e-06 30)" />

        <rect
          x="0.500001"
          y="29.5"
          width="29"
          height="75"
          rx="1.5"
          stroke="var(--stroke-color)"
          transform="rotate(-90 0.500001 29.5)" />
      </svg>
    </template>

    <template v-if="orientation === 'vertical'">
      <svg viewBox="0 0 30 76" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g v-if="isAssetTrailer" class="truck-trailer is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />
          <rect
            x="3.65"
            y="3.65"
            width="22.7"
            height="68.7"
            stroke="var(--dark-fill-color)"
            stroke-width="1.3" />
          <rect
            x="0.396484"
            y="2.15723"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(-45 0.396484 2.15723)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 26 3.07129)" />
          <rect
            x="29.5234"
            y="74.6855"
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="rotate(135 29.5234 74.6855)" />
          <rect
            width="1.3"
            height="3.68304"
            fill="var(--dark-fill-color)"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 0.396484 74.6045)" />
        </g>

        <g v-else-if="isAssetContainer" class="truck-container is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />

          <rect x="4" y="5.55078" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="10.8496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="16.1504" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="21.4492" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="26.75" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="32.0508" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="37.3496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="42.6504" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="47.9492" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="53.25" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="58.5508" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="63.8496" width="22" height="1.3" fill="var(--dark-fill-color)" />
          <rect x="4" y="69.1504" width="22" height="1.3" fill="var(--dark-fill-color)" />
        </g>

        <g v-else-if="isAssetOther" class="truck-other is-empty">
          <rect
            width="30"
            height="76"
            rx="2"
            :fill="isUnknownState ? 'var(--light-fill-color)' : 'white'" />

          <mask
            :id="`truck-other-mask-v-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="28"
            height="74">
            <rect x="1" y="1" width="28" height="20" fill="var(--dark-fill-color)" />
            <rect x="1" y="55" width="28" height="20" fill="var(--dark-fill-color)" />
          </mask>
          <g :mask="`url(#truck-other-mask-v-${svgDefUid})`">
            <path
              d="M0.569236 0.332134L29.061 76.3143"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
            <path
              d="M29.4347 0.332413L1.00302 76.1543"
              stroke="var(--dark-fill-color)"
              stroke-width="1.3" />
          </g>
        </g>

        <!-- Everything under a white pixel will be visible; Everything under a black pixel will be invisible -->
        <mask v-if="true" :id="`container-reveal-mask-v-${svgDefUid}`" mask-type="luminance">
          <rect
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_WIDTH"
            :height="CONTAINER_HEIGHT"
            fill="white" />
          <rect
            id="fillingRevealRectHorizontal"
            class="reveal-mask"
            :x="CONTAINER_X"
            :y="CONTAINER_Y"
            :width="CONTAINER_WIDTH"
            :height="revealWidth"
            fill="black" />
        </mask>

        <g
          v-if="isAssetTrailer"
          class="truck-trailer is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />
          <rect x="3.65" y="3.65" width="22.7" height="68.7" stroke="white" stroke-width="1.3" />
          <rect
            x="0.394531"
            y="2.1582"
            width="1.3"
            height="3.68304"
            fill="white"
            transform="rotate(-45 0.394531 2.1582)" />
          <rect
            width="1.3"
            height="3.68304"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 26 3.07031)"
            fill="white" />
          <rect
            x="29.5234"
            y="74.6855"
            width="1.3"
            height="3.68304"
            transform="rotate(135 29.5234 74.6855)"
            fill="white" />
          <rect
            width="1.3"
            height="3.68304"
            transform="matrix(0.707107 0.707107 0.707107 -0.707107 0.394531 74.6055)"
            fill="white" />
        </g>

        <g
          v-else-if="isAssetContainer"
          class="truck-container is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />

          <rect x="4" y="5.55078" width="22" height="1.3" fill="white" />
          <rect x="4" y="10.8496" width="22" height="1.3" fill="white" />
          <rect x="4" y="16.1504" width="22" height="1.3" fill="white" />
          <rect x="4" y="21.4492" width="22" height="1.3" fill="white" />
          <rect x="4" y="26.75" width="22" height="1.3" fill="white" />
          <rect x="4" y="32.0508" width="22" height="1.3" fill="white" />
          <rect x="4" y="37.3496" width="22" height="1.3" fill="white" />
          <rect x="4" y="42.6504" width="22" height="1.3" fill="white" />
          <rect x="4" y="47.9492" width="22" height="1.3" fill="white" />
          <rect x="4" y="53.25" width="22" height="1.3" fill="white" />
          <rect x="4" y="58.5508" width="22" height="1.3" fill="white" />
          <rect x="4" y="63.8496" width="22" height="1.3" fill="white" />
          <rect x="4" y="69.1504" width="22" height="1.3" fill="white" />
        </g>

        <g
          v-else-if="isAssetOther"
          class="truck-other is-full"
          :mask="`url(#container-reveal-mask-v-${svgDefUid})`">
          <rect width="30" height="76" rx="2" fill="var(--dark-fill-color)" />

          <mask
            :id="`truck-other-mask-v-${svgDefUid}`"
            style="mask-type: alpha"
            maskUnits="userSpaceOnUse"
            x="1"
            y="1"
            width="28"
            height="74">
            <rect x="1" y="1" width="28" height="20" fill="white" />
            <rect x="1" y="55" width="28" height="20" fill="white" />
          </mask>

          <g :mask="`url(#truck-other-mask-v-${svgDefUid})`">
            <path d="M0.569236 0.332134L29.061 76.3143" stroke="white" stroke-width="1.3" />
            <path d="M29.4347 0.332413L1.00302 76.1543" stroke="white" stroke-width="1.3" />
          </g>
        </g>

        <rect v-else width="30" height="76" rx="2" fill="var(--light-fill-color)" />

        <rect x="0.5" y="0.5" width="29" height="75" rx="1.5" stroke="var(--stroke-color)" />
      </svg>
    </template>
  </span>
</template>

<style scoped>
.yard-asset-state-icon-v2 {
  position: relative;

  --light-fill-color: #ffeed9;
  --dark-fill-color: #ff8f00;
  --stroke-color: #c96500;

  opacity: 1;
}

.-horizontal {
  width: 76px;
  height: 30px;
}

.-vertical {
  width: 30px;
  height: 76px;
}

.is-loading {
  opacity: 0.15;
}

svg {
  display: block;
  width: 100%;
  height: 100%;
}

@media (prefers-reduced-motion: no-preference) {
  .reveal-mask {
    transition:
      height 2.5s ease-in-out,
      width 2.5s ease-in-out;
  }

  .yard-asset-state-icon-v2 {
    transition: opacity 0.35s ease-in-out;
  }
}
</style>
